import { useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './AnswerActionButton.module.css';

interface AnswerActionButtonProps {
  children: ReactNode;
  onPress(): void;
  /** The question it answers, so its card can tell its buttons apart. */
  questionId: string;
  /** Nothing to send, the answer already sent, or a request under way. */
  unavailable: boolean;
  variant: 'primary' | 'secondary';
}

/**
 * Save answer or Check answer.
 *
 * Both are unavailable while their request runs, and often after it. A focused
 * button that is disabled drops focus to the page, so a keyboard or
 * screen-reader user who pressed one started again from the top (issue #114).
 * While it has focus, an unavailable button is only marked so, and a press on
 * it does nothing; once focus leaves, it is disabled, so Tab skips it as
 * before.
 */
export function AnswerActionButton({
  children,
  onPress,
  questionId,
  unavailable,
  variant,
}: AnswerActionButtonProps) {
  const [hasFocus, setHasFocus] = useState(false);
  const isMarkedUnavailable = unavailable && hasFocus;

  return (
    <Button
      aria-disabled={isMarkedUnavailable ? 'true' : undefined}
      className={styles.button}
      data-answer-action={questionId}
      disabled={unavailable && !hasFocus}
      onBlur={() => setHasFocus(false)}
      onClick={unavailable ? undefined : onPress}
      onFocus={() => setHasFocus(true)}
      variant={variant}
    >
      {children}
    </Button>
  );
}
